import React, { useEffect, useRef, useState, forwardRef } from 'react';
import { number, objectOf, oneOf, shape, string } from 'prop-types';
import classNames from 'classnames';
import { useParams } from 'react-router-dom';

import { ResponsiveImage } from '../../../../components/index.js';
import css from './CustomAppearance.module.css';

export const CustomAppearance = forwardRef((props, ref) => {
  const {
    id,
    className,
    rootClassName,
    backgroundColor,
    backgroundImage,
    backgroundImageOverlay,
    alt,
    sizes,
  } = props;

  const { country, region, pageId } = useParams();
  const isRegionOrCountryPage = Boolean(country || region || pageId);

  const [isVisible, setIsVisible] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);

  const videoRef = useRef(null);
  const lastPlayTimeRef = useRef(0);

  const getVariantNames = (img) => Object.keys(img?.attributes?.variants || {});

  const backgroundColorStyle = backgroundColor ? { backgroundColor } : {};

  const { preset, color: overlayColor, opacity: overlayOpacity } = backgroundImageOverlay || {};
  const hasBackgroundOverlay = preset && preset !== 'none';
  const overlayStyle = hasBackgroundOverlay ? { backgroundColor: overlayColor, opacity: overlayOpacity } : {};

  // Handle intersection observer and play/pause logic
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const visible = entry.isIntersecting;
        setIsVisible(visible);

        if (visible && isLoaded && document.visibilityState === 'visible' && video.readyState >= 2) {
          video.currentTime = lastPlayTimeRef.current;
          video.play().catch(console.error);
        } else {
          lastPlayTimeRef.current = video.currentTime;
          video.pause();
        }
      },
      { threshold: 0.1 }
    );

    const handleLoadedData = () => setIsLoaded(true);
    const handleVisibilityChange = () => {
      if (document.visibilityState !== 'visible') {
        if (video.readyState >= 2) {
          lastPlayTimeRef.current = video.currentTime;
          video.pause();
        }
      }
    };

    video.addEventListener('loadeddata', handleLoadedData);
    observer.observe(video);
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      video.removeEventListener('loadeddata', handleLoadedData);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      observer.disconnect();
    };
  }, [isLoaded]);

  // Handle Safari and iOS quirks
  useEffect(() => {
    const video = videoRef.current;
    const isSafari =
      typeof window !== 'undefined' &&
      /^((?!chrome|android).)*safari/i.test(window.navigator.userAgent);

    const isIOS =
      typeof window !== 'undefined' &&
      /iPad|iPhone|iPod/.test(window.navigator.userAgent) &&
      !window.MSStream;

    if (!video || !(isSafari || isIOS)) return;

    video.muted = true;
    video.load();

    if (isIOS) {
      setTimeout(() => {
        if (isVisible && video) {
          const playPromise = video.play();
          if (playPromise) {
            playPromise.catch(() => {
              const interactionHandler = () => {
                video.play().catch(console.error);
                ['touchstart', 'click'].forEach(event =>
                  document.removeEventListener(event, interactionHandler)
                );
              };

              ['touchstart', 'click'].forEach(event =>
                document.addEventListener(event, interactionHandler, { once: true })
              );
            });
          }
        }
      }, 100);
    } else {
      if (isVisible && video) {
        video.play().catch(() => console.log('Safari autoplay blocked'));
      }
    }
  }, [isVisible]);

  const classes = classNames(rootClassName || css.backgroundImageWrapper, className);

  return (
    <div className={classes} style={backgroundColorStyle}>
      {id === 'landing-hero' && !isRegionOrCountryPage ? (
        <video
          ref={videoRef}
          className={css.backgroundImage}
          loop
          muted
          playsInline
          preload="metadata"
          controls={false}
          webkit-playsinline="true"
          poster={process.env.REACT_APP_LANDING_PAGE_VIDEO_POSTER}
          onError={(e) => {
            console.log('Video error:', e);
            setIsLoaded(false);
          }}
          onLoadedMetadata={() => {
            videoRef.current?.play().catch((e) => console.log('Metadata play failed:', e));
          }}
        >
          <source src={process.env.REACT_APP_LANDING_PAGE_VIDEO_URL} type="video/mp4" />
        </video>
      ) : backgroundImage ? (
        <ResponsiveImage
          ref={ref}
          className={css.backgroundImage}
          alt={alt}
          image={backgroundImage}
          variants={getVariantNames(backgroundImage)}
          sizes={sizes}
        />
      ) : null}

      {hasBackgroundOverlay && <div className={css.backgroundOverlay} style={overlayStyle} />}
    </div>
  );
});

CustomAppearance.displayName = 'CustomAppearance';

CustomAppearance.defaultProps = {
  rootClassName: null,
  className: null,
  alt: 'background image',
  sizes: null,
  backgroundColor: null,
  backgroundImage: null,
  backgroundImageOverlay: null,
};

CustomAppearance.propTypes = {
  rootClassName: string,
  className: string,
  backgroundColor: string,
  backgroundImage: shape({
    id: string.isRequired,
    type: oneOf(['imageAsset']).isRequired,
    attributes: shape({
      variants: objectOf(
        shape({
          width: number.isRequired,
          height: number.isRequired,
          url: string.isRequired,
        })
      ).isRequired,
    }).isRequired,
  }),
  backgroundImageOverlay: shape({
    preset: string.isRequired,
    color: string.isRequired,
    opacity: number.isRequired,
  }),
  alt: string,
  sizes: string,
};
